import {
  type SegmentLoaderOptionsType,
  loadSegmentLibrary,
} from './segment.loader';
import { getCookieSubdomainString } from '../datadog/cookies.datadog';
import { getSegmentAnonymousIdFromCookie } from './cookies.segment';

declare global {
  interface Window {
    /**
     * @link https://developers.intercom.com/installing-intercom/web/attributes-objects/
     */
    intercomSettings: {
      hide_default_launcher: boolean;
      horizontal_padding: number;
      vertical_padding: number;
    };
    /**
     * Flag to track if Segment error handlers have been set up
     * @internal
     */
    __segmentErrorHandlerSetup?: boolean;
  }
}

type LoadSegmentType = {
  apiKey: string;
  src: string;
  isProvider: boolean;
};

export const loadSegment = ({
  apiKey,
  src,
  isProvider,
}: LoadSegmentType): void => {
  window.intercomSettings = {
    /**
     * We have our own HelpButton and HelpMenu.
     */
    hide_default_launcher: true,

    /**
     * Position the Intercom Messenger to the left of our HelpButton.
     */
    horizontal_padding: 80,
    vertical_padding: 4,
  };

  // Configure cookie domain for anonymousId storage
  const cookieDomain = getCookieSubdomainString();

  // Prioritize root domain cookie over localStorage
  const anonymousIdFromCookie = getSegmentAnonymousIdFromCookie();

  const options: SegmentLoaderOptionsType = {
    cookie: {
      domain: cookieDomain,
    },
    // Patient must not see Intercom help button.
    // @see https://doxyme.atlassian.net/browse/CS-12380
    ...(isProvider
      ? {}
      : {
          integrations: { Intercom: false },
        }),
  };

  /**
   * Checks if an error is a Segment Analytics.js loading error.
   */
  const isSegmentLoadingError = (message: unknown): boolean => {
    return (
      typeof message === 'string' &&
      message.includes('[analytics.js]') &&
      message.includes('Failed to load Analytics.js')
    );
  };

  /**
   * Sets up global error handlers to catch and suppress Segment Analytics.js loading errors.
   * This prevents unhandled errors from appearing in the console when Segment fails to load.
   * The retry mechanism in segment.loader.ts will handle retries automatically.
   */
  const setupSegmentErrorHandlers = (): void => {
    // Handle unhandled promise rejections from Segment
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      if (
        reason &&
        typeof reason === 'object' &&
        'message' in reason &&
        isSegmentLoadingError(reason.message)
      ) {
        event.preventDefault();
      }
    });

    // Handle synchronous errors from Segment
    const originalErrorHandler = window.onerror;
    window.onerror = (
      message: string | Event,
      source?: string,
      lineno?: number,
      colno?: number,
      error?: Error,
    ): boolean => {
      if (isSegmentLoadingError(message)) {
        return true;
      }
      return originalErrorHandler
        ? originalErrorHandler(message, source, lineno, colno, error)
        : false;
    };
  };

  // Only set up error handlers once to avoid duplicate handlers
  if (!window.__segmentErrorHandlerSetup) {
    setupSegmentErrorHandlers();
    window.__segmentErrorHandlerSetup = true;
  }

  loadSegmentLibrary(apiKey, src, options);

  /**
   * Enrich every Segment event (track, page, identify, etc.) with shared context:
   * - `is_apollo` — this app is v2
   * - `loaded_in_iframe` — whether the page is running inside an iframe.
   *
   * @docs https://www.twilio.com/docs/segment/connections/sources/catalog/libraries/website/javascript/middleware
   */
  const analytics = window.analytics as unknown as
    | {
        addSourceMiddleware: (
          fn: (arg: {
            payload: { obj: Record<string, unknown> };
            next: (p: unknown) => void;
          }) => void,
        ) => void;
      }
    | undefined;
  if (analytics?.addSourceMiddleware) {
    analytics.addSourceMiddleware(
      ({
        payload,
        next,
      }: {
        payload: { obj: Record<string, unknown> };
        next: (p: unknown) => void;
      }) => {
        const loaded_in_iframe = window.self !== window.top;
        if (payload.obj) {
          const ctx = payload.obj['context'] ?? {};
          payload.obj['context'] = {
            ...ctx,
            is_apollo: true,
            loaded_in_iframe,
          };
        }
        next(payload);
      },
    );
  }

  // Ensure cookie value takes precedence after Segment initializes
  if (anonymousIdFromCookie && window.analytics?.ready) {
    window.analytics.ready(() => {
      window.analytics.setAnonymousId?.(anonymousIdFromCookie);
    });
  }
};
